import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { StarRating } from 'react-flexible-star-rating';
import { useFormikContext } from 'formik';
import {
  ChecksGroup,
  Editable,
  FormManager,
  Grid,
  Initialization,
  nullObjectIfEmpty,
  PageTitle,
  Richtext,
  Section,
  Update,
  useCacheQuery,
  useCacheRecord,
  useCloneEntity,
  validateAll,
} from 'gossamer-front-end';
import { FeedbackForm, FeedbackFormRow } from 'cb-model';
import { FieldType, getFilter, getListRowLink } from 'gossamer-unified';
import { AllValues, FormRow } from 'gossamer-front-end';
import { FeedbackFormQuestion, FeedbackFormQuestionRow } from 'cb-model';
import { FeedbackFormLogic } from './FeedbackFormCreate';

export const FeedbackFormUpdate = (): React.JSX.Element => {
  const params = useParams();
  const navigate = useNavigate();
  const record = useCacheRecord(FeedbackForm, params.key);
  const query = useCacheQuery(FeedbackFormQuestion, {
    where: { and: [getFilter('feedback_form_id', 'eq', params.key)] },
  });
  const handleExit = () => navigate(getListRowLink(FeedbackForm.listRowLink, params.key));
  const LocalFeedbackForm = useCloneEntity(FeedbackForm);
  const initialization: Initialization = {
    main: { type: 'update-single', entity: LocalFeedbackForm, record },
    questions: { type: 'update-multi', entity: FeedbackFormQuestion, query },
  };
  const validate = (values: AllValues) => {
    const out = validateAll(initialization, values) || {};
    const standardFieldUsed: Record<string, boolean> = {};
    (values.questions as FormRow<FeedbackFormQuestionRow>[]).forEach(
      (question: FormRow<FeedbackFormQuestionRow>, index: number) => {
        const stdField = question.standard_field;
        if (stdField) {
          if (question.question_type !== 'star_rating') {
            out.questions = out.questions || [];
            out.questions[index] = out.questions[index] || {};
            out.questions[index].standard_field = `question type must be 'star rating'`;
          }
          if (standardFieldUsed[stdField]) {
            out.questions = out.questions || [];
            out.questions[index] = out.questions[index] || {};
            out.questions[index].standard_field = `${stdField} already used above`;
          }
          standardFieldUsed[stdField] = true;
        }
      },
    );
    return nullObjectIfEmpty(out);
  };
  return (
    <>
      <PageTitle verb="Update" entity={FeedbackForm} keyValue={params.key} />
      <FormManager
        compoundUrl="/main/~/feedback-form-update"
        handleCancel={handleExit}
        handleSuccess={handleExit}
        initialization={initialization}
        validate={validate}
      >
        {/* <div style={{ display: 'flex', flexBasis: 'row wrap', gap: 20 }}>
          <div style={{ width: '50%' }}> */}
        <FeedbackFormLogic entity={LocalFeedbackForm} />
        <Update valuesKey="main" entity={LocalFeedbackForm} />
        <ShowOnlyIfInternalFormRequired>
          <Grid
            valuesKey="questions"
            entity={FeedbackFormQuestion}
            onAddRow={(newData) => {
              newData.feedback_form_id = parseInt(params.key);
            }}
          />
          {/* </div>
          <div style={{ width: '50%' }}> */}
          <ExampleForm />
          {/* </div>
        </div> */}
        </ShowOnlyIfInternalFormRequired>
      </FormManager>
    </>
  );
};

const ShowOnlyIfInternalFormRequired = ({ children }: { children: React.ReactNode }): React.ReactNode => {
  const { values } = useFormikContext<{ main: FormRow<FeedbackFormRow> }>();
  if (values.main.internal_external_mode === 'external') {
    return null;
  }
  return children;
};

const ExampleForm = () => {
  const { values } = useFormikContext<{ questions: FormRow<FeedbackFormQuestionRow>[] }>();
  const out = values.questions
    .slice()
    .filter((row) => !row._deleting)
    .sort((a, b) => a.seq - b.seq)
    .map((config) => {
      return (
        <div key={config.id}>
          <div>
            <b>{config.title}</b>: {config.subtitle}&nbsp;{config.is_mandatory ? '*' : ''}
          </div>
          {config.question_type === 'star_rating' && <StarRating dimension={10} />}
          {config.question_type === 'yes_no' && (
            <ChecksGroup
              type="radio"
              inline={true}
              onItemSelected={() => {}}
              mandatory={config.is_mandatory}
              options={[
                { id: 'yes', label: 'yes' },
                { id: 'no', label: 'no' },
              ]}
              value=""
            />
          )}
          {config.question_type === 'short_text' && (
            <Editable handleChange={() => {}} value="" field={{ seq: 0, type: FieldType.Text, label: '' }} />
          )}
          {config.question_type === 'long_text' && (
            <Richtext handleChange={() => {}} mandatory={config.is_mandatory} value="" />
          )}
          <br />
        </div>
      );
    });

  return (
    <Section
      title="Resulting Form"
      subtitle="This is what the feedback form will look like to a user, based on the above configuration"
    >
      <div>{out}</div>
    </Section>
  );
};
